/**
 * <ActivityCorrectionMarker> — la pastille numérotée d'un point, posée sur la copie (molecule).
 *
 * Le numéro du point dans l'aplat de son type — le même visage que le numéro de sa carte
 * (`CORRECTION_POINT_FACES`). Un liseré blanc et une ombre la détachent de la photo : elle se lit
 * comme un bouton, pas comme une tache de la copie. Choisie, elle grossit un peu.
 *
 * Ne se place pas : l'appelant la positionne (`style`), parce que lui seul connaît la page.
 *
 * Sans `onPress`, c'est la même pastille posée à plat — l'en-tête de la carte du point : ni liseré
 * ni ombre (elle n'a rien à détacher), et pas un bouton pour un lecteur d'écran.
 */
import React from "react"
import { Pressable, View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UIActivityCorrectionPointType } from "../../../ports/primary/ui/types"
import { badgeFace } from "../../atoms/Badge/badgeFace"
import { Text } from "../../atoms/typography/Text"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { shadowStyle } from "../../utils/shadow"
import { CORRECTION_POINT_FACES } from "../ActivityCorrectionPoint/correctionPointFaces"

/** Le diamètre de la pastille, en points — l'appelant en retire la moitié pour la centrer. */
export const CORRECTION_MARKER_SIZE = 28

export type ActivityCorrectionMarkerProps = {
    index: number
    type: UIActivityCorrectionPointType
    selected?: boolean
    onPress?: () => void
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function ActivityCorrectionMarker(props: ActivityCorrectionMarkerProps) {
    const { index, type, selected = false, onPress, style, testID } = props
    const face = CORRECTION_POINT_FACES[type]
    const { bg, ink } = badgeFace(face.badge, false)

    const number = (
        <Text size="text-xs" weight="bold" color={ink}>
            {index}
        </Text>
    )

    if (onPress == null) {
        return (
            <View
                accessibilityLabel={`Point ${index}`}
                style={[styles.marker(bg, false, false), style]}
                testID={testID}
            >
                {number}
            </View>
        )
    }

    return (
        <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Point ${index} — ${face.label}`}
            accessibilityState={{ selected }}
            hitSlop={8}
            onPress={onPress}
            style={[styles.marker(bg, selected, true), style]}
            testID={testID}
        >
            {number}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    marker: (bg: SemanticColorKey, selected: boolean, onCopy: boolean) => ({
        width: CORRECTION_MARKER_SIZE,
        height: CORRECTION_MARKER_SIZE,
        borderRadius: CORRECTION_MARKER_SIZE / 2,
        backgroundColor: theme.colors[bg],
        alignItems: "center" as const,
        justifyContent: "center" as const,
        // Sur la photo, un liseré blanc et une ombre la détachent de l'écriture.
        ...(onCopy
            ? {
                  borderWidth: 2,
                  borderColor: theme.colors.white,
                  transform: [{ scale: selected ? 1.18 : 1 }],
                  ...shadowStyle(theme, "floating"),
              }
            : {}),
    }),
}))
